/**
 * Root App Component
 * SoundOps web console.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import AppRoutes from './AppRoutes';
import ErrorBoundary from './components/common/ErrorBoundary';
import OfflineBanner from './components/common/OfflineBanner';
import UiDebugInitializer from './components/common/UiDebugInitializer';
import AuthSessionManager from './components/common/AuthSessionManager';

// Create React Query client with enhanced error handling
type HttpErrorLike = {
  response?: {
    status?: number;
  };
};

const getHttpStatus = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const { response } = error as HttpErrorLike;
    if (response && typeof response.status === 'number') {
      return response.status;
    }
  }
  return undefined;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        const status = getHttpStatus(error);
        // Don't retry on 401 (authentication errors) or 404 (not found)
        if (status === 401 || status === 404) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      refetchOnWindowFocus: false,
      staleTime: 5000, // 5 seconds
    },
    mutations: {
      retry: (failureCount, error: unknown) => {
        const status = getHttpStatus(error);
        // Don't retry on 4xx errors (client errors)
        if (status && status >= 400 && status < 500) {
          return false;
        }
        // Retry once for server errors (5xx)
        return failureCount < 1;
      },
      retryDelay: 1000,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <UiDebugInitializer />
        <AuthSessionManager />
        <BrowserRouter>
          {/* Offline indicator */}
          <OfflineBanner />

          <AppRoutes />
        </BrowserRouter>

        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            className: 'so-toast',
            style: {
              background: 'var(--so-color-inverse-surface)',
              color: 'var(--so-color-on-inverse)',
              fontFamily: 'var(--so-font-ui)',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: 'var(--so-status-ready-fg)',
                secondary: 'var(--so-color-inverse-surface)',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: 'var(--so-color-danger)',
                secondary: 'var(--so-color-on-danger)',
              },
            },
          }}
        />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
